/**
 * Killa FX: progressive motion enhancements paired with snippets/killa-fx-styles.liquid.
 * - Reveal fallback (IntersectionObserver) where scroll-driven animations are unsupported.
 * - Pauses looping animations inside [data-kfx-loop] while they're offscreen.
 * - Pointer spotlight on [data-kfx-spotlight] hosts.
 * - Magnetic pull on [data-kfx-magnet] and 3D tilt on [data-kfx-tilt] (fine pointers only).
 * Everything is skipped when the visitor prefers reduced motion.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

function initRevealFallback() {
  if (CSS.supports('animation-timeline: view()')) return;
  if (document.documentElement.classList.contains('kfx-static')) return;
  const targets = document.querySelectorAll('.scroll-reveal, [data-kfx-reveal]');
  if (!targets.length) return;
  document.documentElement.classList.add('kfx-io');
  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-in');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  targets.forEach((el) => observer.observe(el));
}

// Offscreen marquees and reels keep burning frames otherwise, which steals smoothness from what's on screen.
function initLoopPausing() {
  const loops = document.querySelectorAll('[data-kfx-loop]');
  if (!loops.length || !('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) entry.target.classList.toggle('kfx-paused', !entry.isIntersecting);
    },
    { rootMargin: '100px 0px' }
  );
  loops.forEach((el) => observer.observe(el));
  document.addEventListener('visibilitychange', () => {
    loops.forEach((el) => {
      if (document.hidden) el.classList.add('kfx-paused');
    });
    if (!document.hidden) loops.forEach((el) => {
      observer.unobserve(el);
      observer.observe(el);
    });
  });
}

// Coalesces pointer events into one write per animation frame.
function onFramePointer(el, handler) {
  let frame = 0;
  let last = null;
  el.addEventListener('pointerenter', () => el.classList.add('is-tracking'));
  el.addEventListener('pointermove', (event) => {
    last = event;
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      handler(last, el.getBoundingClientRect());
    });
  });
  el.addEventListener('pointerleave', () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    el.classList.remove('is-tracking');
    el.style.transform = '';
  });
}

function initSpotlights() {
  document.querySelectorAll('[data-kfx-spotlight]').forEach((host) => {
    onFramePointer(host, (event, rect) => {
      host.style.setProperty('--kfx-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
      host.style.setProperty('--kfx-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    });
  });
}

function initMagnets() {
  document.querySelectorAll('[data-kfx-magnet]').forEach((el) => {
    onFramePointer(el, (event, rect) => {
      const x = event.clientX - rect.left - rect.width / 2;
      const y = event.clientY - rect.top - rect.height / 2;
      el.style.transform = `translate3d(${x * 0.2}px, ${y * 0.3}px, 0)`;
    });
  });
}

function initTilt() {
  document.querySelectorAll('[data-kfx-tilt]').forEach((el) => {
    onFramePointer(el, (event, rect) => {
      const px = (event.clientX - rect.left) / rect.width - 0.5;
      const py = (event.clientY - rect.top) / rect.height - 0.5;
      el.style.transform = `perspective(900px) rotateX(${py * -5}deg) rotateY(${px * 6}deg)`;
    });
  });
}

function init() {
  initRevealFallback();
  if (reduceMotion.matches) return;
  initLoopPausing();
  if (!finePointer.matches) return;
  initSpotlights();
  initMagnets();
  initTilt();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
